"use client";

import type { KycMediaValue } from "./kyc-media-field";

const DB_NAME = "mistry-bhai-kyc-media";
const STORE = "files";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveKycMedia(key: string, value: KycMediaValue) {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    if (!value) tx.objectStore(STORE).delete(key);
    else {
      tx.objectStore(STORE).put({
        key,
        name: value.file.name,
        type: value.file.type,
        source: value.source,
        capturedAt: value.capturedAt,
        blob: value.file,
      }, key);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadKycMedia(key: string): Promise<KycMediaValue> {
  const db = await openDb();
  const record = await new Promise<Record<string, unknown> | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const request = tx.objectStore(STORE).get(key);
    request.onsuccess = () => resolve(request.result as Record<string, unknown> | undefined);
    request.onerror = () => reject(request.error);
  });
  db.close();
  if (!record?.blob) return null;
  const blob = record.blob as Blob;
  const name = String(record.name || `${key}.jpg`);
  const type = String(record.type || blob.type || "image/jpeg");
  const file = new File([blob], name, { type });
  return {
    file,
    source: record.source === "camera" ? "camera" : "file",
    previewUrl: type.startsWith("image/") ? URL.createObjectURL(file) : "",
    capturedAt: Number(record.capturedAt || Date.now()),
  };
}

export async function clearKycMedia() {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
